import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Check, ChevronDown } from "lucide-react";
import { useMyOrganizations, useSelectOrganization } from "../cacheQueries";
import { pickActiveOrganizationId } from "../utils/orgMembership";
import { useClickOutside } from "../hooks/useClickOutside";

interface OrgSwitcherProps {
  // Kept as the visible label so the navbar text doesn't flicker between
  // membership-list values and the company-details fetch (they should agree,
  // but the latter has been the long-time source of truth here).
  fallbackName: string;
  onBeforeOpen?: () => void;
}

export default function OrgSwitcher({ fallbackName, onBeforeOpen }: OrgSwitcherProps) {
  const { t } = useTranslation();
  const { data: orgs, refetch: refetchOrgs } = useMyOrganizations();
  const selectOrg = useSelectOrganization();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  useClickOutside(containerRef, open, () => setOpen(false));

  // Focus the LIST when the menu opens — not its first item. Programmatic
  // focus on the item drew the browser's default focus ring on every open,
  // mouse included, and the container's overflow-hidden clipped that ring's
  // vertical edges into two floating lines around the first row. Keyboard
  // users land inside either way; ArrowDown reaches the first item.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.focus();
  }, [open]);

  const memberships = orgs ?? [];
  const isMultiOrg = memberships.length > 1;
  const activeOrgId = pickActiveOrganizationId(memberships);
  const activeOrg = memberships.find((o) => o.organization_id === activeOrgId);
  const displayName = activeOrg?.name ?? fallbackName;

  const closeMenu = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  // Refetch the membership list every time the menu opens so a just-added org
  // shows up immediately — the query has a 5-min staleTime and no focus
  // refetch, so the cached list could otherwise be minutes behind the DB.
  const onOpening = () => {
    onBeforeOpen?.();
    void refetchOrgs();
  };

  const handleOpen = () => {
    if (!isMultiOrg) return;
    if (!open) onOpening();
    setOpen((v) => !v);
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) {
        onOpening();
        setOpen(true);
      }
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      closeMenu();
    }
  };

  // Keyboard navigation within the open menu.
  const handleMenuKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>(
        "button[role='menuitem']",
      ) ?? [],
    );
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);

    if (e.key === "Escape") {
      e.preventDefault();
      closeMenu();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(currentIndex + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      // From the list itself (nothing focused yet, index -1) Up wraps to the
      // LAST item; the modulo alone would land second-to-last.
      items[currentIndex === -1 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Tab") {
      // Close without returning focus so Tab's default behaviour moves focus
      // naturally to the next element in the page.
      closeMenu(false);
    }
  };

  const handleSelect = async (organizationId: number) => {
    if (selectOrg.isPending) return;
    if (organizationId === activeOrgId) {
      closeMenu();
      return;
    }
    try {
      await selectOrg.mutateAsync(organizationId);
    } catch (e) {
      console.error("selectOrganization failed", e);
    }
    closeMenu();
  };

  // Single-org users see the static label they always had — no dropdown
  // affordance, no caret, no click target.
  if (!isMultiOrg) {
    return (
      <>
        <Building2 className="w-5 h-5 opacity-75 max-sm:hidden" />
        <span
          data-testid="org-switcher-static-label"
          className="capitalize font-medium pr-2 lg:pr-6 text-white text-opacity-75 max-sm:hidden"
        >
          {displayName}
        </span>
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="org-switcher-trigger"
        aria-label={t("orgSelection.switchOrg", "Switch organization")}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        className={`
          flex items-center gap-1 sm:gap-1.5 px-1.5 sm:pl-2 sm:pr-2 lg:pr-3 py-1 rounded-lg
          focus:outline-none focus:ring-0 transition-colors
          ${open
            ? "bg-white text-gray-800"
            : "bg-white/0 hover:bg-white/15 text-white text-opacity-90"}
        `}
      >
        <Building2 className="w-5 h-5 opacity-90 shrink-0" />
        {/* Hide the name on tight phone widths; cap it progressively above.
            A flat 12rem cap truncated "AirPlate Cloud Development" even on a
            wide desktop where the navbar had half its width to spare — the cap
            exists to protect mid-width layouts, not to clip when space is
            free. 24rem fits any real org name; the dropdown always shows the
            full name regardless. */}
        <span className="capitalize font-medium truncate max-w-[12rem] lg:max-w-[24rem] max-sm:hidden">
          {displayName}
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-[120]">
          <div className="px-4 py-2 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
            {t("orgSelection.title", "Select organization")}
          </div>
          <ul
            ref={menuRef}
            role="menu"
            aria-label={t("orgSelection.switchOrg", "Switch organization")}
            tabIndex={-1}
            className="max-h-80 overflow-y-auto py-1 outline-none"
            onKeyDown={handleMenuKeyDown}
          >
            {memberships.map((org) => {
              const isActive = org.organization_id === activeOrgId;
              const isSubmitting = selectOrg.variables === org.organization_id;
              return (
                <li key={org.organization_id} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    data-testid={`org-switcher-item-${org.organization_id}`}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => handleSelect(org.organization_id)}
                    disabled={selectOrg.isPending}
                    className={`
                      w-full flex items-center justify-between gap-3 px-4 py-2.5
                      text-sm transition-colors focus:outline-none
                      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-secondary
                      focus:bg-gray-50
                      ${isActive ? "text-secondary font-semibold" : "text-gray-800 hover:bg-gray-50"}
                      disabled:opacity-60 disabled:cursor-not-allowed
                    `}
                  >
                    <span className="truncate text-left">{org.name}</span>
                    {isActive ? (
                      <Check className="w-4 h-4 shrink-0" />
                    ) : isSubmitting ? (
                      <span className="text-xs text-gray-500 shrink-0">
                        {t("orgSelection.selecting", "Selecting…")}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
